import { useState } from 'react';
import { ActionIcon, Badge, Box, Button, Group, Paper, Stack, Table, Text } from '@mantine/core';
import { IconCheck, IconClock, IconFileDescription, IconLink, IconPlus, IconTrash, IconX } from '@tabler/icons-react';
import { useSubscription } from 'urql';
import { ErrorAlert } from '@/components/error-alert';
import { api } from '@/lib/api';
import { getErrorMessage } from '@/lib/error-message';
import { AddDocumentModal } from './add-document-modal';
import { PersonaDocumentsSubscription } from './graphql';

const statusBadges: Record<string, { color: string; icon: typeof IconCheck; label: string }> = {
  indexed: { color: 'green', icon: IconCheck, label: 'Indexed' },
  processing: { color: 'blue', icon: IconClock, label: 'Processing' },
  pending: { color: 'gray', icon: IconClock, label: 'Pending' },
  failed: { color: 'red', icon: IconX, label: 'Failed' },
};

function DocumentStatusBadge({ status, error }: { status: string; error: string | null }) {
  const badge = statusBadges[status];

  if (!badge) return <Badge>{status}</Badge>;

  return (
    <Badge color={badge.color} leftSection={<badge.icon size={12} />} title={error ?? undefined}>
      {badge.label}
    </Badge>
  );
}

export function PersonaKnowledgePanel({ personaId }: { personaId: string }) {
  const [{ data, error: subscriptionError }] = useSubscription({
    query: PersonaDocumentsSubscription,
    variables: { personaId },
  });

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleRemove = async (documentId: string) => {
    setDeletingId(documentId);
    setError(null);

    const result = await api.personas({ personaId }).documents({ documentId }).delete();

    setDeletingId(null);

    if (result.error) setError(getErrorMessage(result.error, 'Failed to remove document'));
  };

  const documents = data?.personaDocuments ?? [];

  return (
    <>
      <Paper p="lg" withBorder radius="lg">
        <Stack gap="md">
          <ErrorAlert
            message={
              error ?? (subscriptionError ? getErrorMessage(subscriptionError, 'Failed to load documents') : null)
            }
          />
          <Group justify="space-between">
            <div>
              <Text fw={500}>Knowledge Base</Text>
              <Text size="sm" c="dimmed">
                Add documents and URLs to give the AI context-specific knowledge.
              </Text>
            </div>
            <Button leftSection={<IconPlus size={16} />} onClick={() => setAddModalOpen(true)}>
              Add Document
            </Button>
          </Group>

          {documents.length === 0 ? (
            <Box p="xl" ta="center" c="dimmed">
              <Text>No documents in knowledge base.</Text>
              <Text size="sm">Add documents to give your AI agent specialized knowledge.</Text>
            </Box>
          ) : (
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Name</Table.Th>
                  <Table.Th>Type</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th w={60} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {documents.map((doc) => (
                  <Table.Tr key={doc.id}>
                    <Table.Td>
                      <Group gap="xs">
                        {doc.type === 'url' ? <IconLink size={16} /> : <IconFileDescription size={16} />}
                        {doc.name}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Badge variant="light" size="sm">
                        {doc.type}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <DocumentStatusBadge status={doc.status} error={doc.error ?? null} />
                    </Table.Td>
                    <Table.Td>
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        loading={deletingId === doc.id}
                        onClick={() => handleRemove(doc.id)}
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Stack>
      </Paper>

      <AddDocumentModal personaId={personaId} opened={addModalOpen} onClose={() => setAddModalOpen(false)} />
    </>
  );
}
